import React, { useState, useEffect, Suspense } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowRight, BookOpen } from 'lucide-react';
import { useCourseApi, Course } from '../hooks/useCourseApi';
import CourseCard from '../components/course/CourseCard';

const Preparatory3CoursesPage: React.FC = () => {
  const { grade } = useParams<{ grade: string }>();
  const location = useLocation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { getCourses, isLoading, error } = useCourseApi();

  const gradeValue = 'Preparatory3';
  const gradeTitle = 'الصف الثالث الإعدادي';

  useEffect(() => {
    fetchCourses();
  }, [gradeValue, pageNumber]);

  const fetchCourses = async () => {
    try {
      const result = await getCourses(gradeValue, pageNumber, 10);
      
      if (result?.success && result?.data) {
        setCourses(result.data.courses || []);
        setTotalPages(result.data.totalPages || 1);
      } else {
        setCourses([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
      setCourses([]);
      setTotalPages(1);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>كورسات {gradeTitle} - منصة مستر وليد المصري | تعليم اللغة الإنجليزية</title>
        <meta name="title" content={`كورسات ${gradeTitle} - منصة مستر وليد المصري | تعليم اللغة الإنجليزية`} />
        <meta name="description" content={`اكتشف أفضل كورسات اللغة الإنجليزية لـ ${gradeTitle} مع مستر وليد المصري. دروس شاملة ومفصلة تغطي جميع أجزاء المنهج مع شرح تفصيلي وتمارين تطبيقية.`} />
        <meta name="keywords" content={`كورسات ${gradeTitle}, تعليم انجليزي, دروس انجليزي, مستر وليد المصري, منصة تعليمية, انجليزي اعدادي, شرح انجليزي, تمارين انجليزي`} />
        <meta name="author" content="مستر وليد المصري" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://mohamed-said.com/courses/${grade}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://mohamed-said.com/courses/${grade}`} />
        <meta property="og:title" content={`كورسات ${gradeTitle} - منصة مستر وليد المصري`} />
        <meta property="og:description" content={`اكتشف أفضل كورسات اللغة الإنجليزية لـ ${gradeTitle} مع مستر وليد المصري. دروس شاملة ومفصلة تغطي جميع أجزاء المنهج.`} />
        <meta property="og:image" content="https://mohamed-said.com/favicon.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content={`كورسات ${gradeTitle} - منصة مستر وليد المصري`} />
        <meta property="og:site_name" content="منصة مستر وليد المصري التعليمية" />
        <meta property="og:locale" content="ar_AR" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://mohamed-said.com/courses/${grade}`} />
        <meta property="twitter:title" content={`كورسات ${gradeTitle} - منصة مستر وليد المصري`} />
        <meta property="twitter:description" content={`اكتشف أفضل كورسات اللغة الإنجليزية لـ ${gradeTitle} مع مستر وليد المصري.`} />
        <meta property="twitter:image" content="https://mohamed-said.com/favicon.png" />
        <meta property="twitter:creator" content="@mohamed_said" />

        {/* Additional SEO Meta Tags */}
        <meta name="language" content="Arabic" />
        <meta name="geo.region" content="EG" />
        <meta name="subject" content={`تعليم اللغة الإنجليزية لـ ${gradeTitle}`} />
        <meta name="category" content="تعليم" />
        <meta name="classification" content="منصة تعليمية" />

        {/* Structured Data for Course List */}
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": `كورسات ${gradeTitle} - منصة مستر وليد المصري`,
          "description": `مجموعة شاملة من كورسات اللغة الإنجليزية لـ ${gradeTitle}`,
          "url": `https://mohamed-said.com/courses/${grade}`,
          "numberOfItems": courses.length,
          "itemListElement": courses.map((course, index) => ({
            "@type": "Course",
            "position": index + 1,
            "name": course.courseName,
            "description": course.description || `دورة شاملة في اللغة الإنجليزية لـ ${gradeTitle}`,
            "provider": {
              "@type": "Person",
              "name": "مستر وليد المصري"
            },
            "educationalLevel": gradeTitle,
            "inLanguage": "ar",
            "teaches": "اللغة الإنجليزية"
          }))
        })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-white" dir="rtl">
      {/* Header Section */}
      <div className="py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-gray-500 mb-8" dir="ltr">
            <Link 
              to="/" 
              className="hover:text-orange-500 transition-colors duration-200 flex items-center"
            >
              الرئيسية
              <ArrowRight className="w-4 h-4 mx-2" />
            </Link>
            <span className="text-gray-900">كورسات {gradeTitle}</span>
          </nav>

          {/* Page Title */}
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-cairo-bold text-gray-900 mb-4">
              كورسات {gradeTitle}
            </h1>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-4 rounded-full"></div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
             أهلا بيك يا عبقري   
            </p>
          </div>
        </div>
      </div>

      {/* Courses Section */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-orange-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-500 font-cairo-medium">جاري تحميل الكورسات...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-md mx-auto shadow-sm">
                <p className="text-gray-800 font-cairo-medium mb-4">{error}</p>
                <button
                  onClick={fetchCourses}
                  className="px-6 py-2 bg-white border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors duration-200 font-cairo-medium"
                >
                  إعادة المحاولة
                </button>
              </div>
            </div>
          )}

          {!isLoading && !error && courses.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 font-cairo-medium">
                لا توجد كورسات متاحة لـ{gradeTitle} حالياً
              </p>
            </div>
          )}

          {!isLoading && !error && courses.length > 0 && (
            <>
              {/* Courses Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                {courses.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-10">
                  <div className="inline-flex rounded-lg border border-gray-200 divide-x rtl:divide-x-reverse">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 transition-colors duration-200 font-cairo-medium ${
                          pageNumber === page
                            ? 'bg-orange-50 text-orange-600'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default Preparatory3CoursesPage;
